import { DestroyRef, inject, NgZone, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppRoutes, ENV_CONFIG, EnvConfig, MessagesRoutes, USER_DATA_TOKEN, UserDataProvider, UserModel } from '@core';
import {
  BehaviorSubject,
  EMPTY,
  expand,
  filter,
  from,
  last,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  tap
} from 'rxjs';
import { chatTranslation } from 'src/assets/i18n/chat-translation';
import { ActiveChannelModel } from '../interfaces';
import { Router } from '@angular/router';
import { ChatChannelType } from '@core-http';
import { Channel, LocalMessage } from 'stream-chat';
import { ClientEvent } from 'stream-chat-angular';
import {
  STREAM_CHANNEL_TOKEN,
  STREAM_CHAT_CLIENT_TOKEN,
  StreamChannelProvider,
  StreamChatClientProvider
} from '../../infrastructure/tokens';
import { StreamChatEvent } from '../enums';

const CHAT_PAGE_SIZE: number = 30;
const FIND_CHANNEL_PAGE_SIZE: number = 30;

export class ChatInitializer {
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly router: Router = inject(Router);
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly channelProvider: StreamChannelProvider = inject(STREAM_CHANNEL_TOKEN);
  private readonly chatClientProvider: StreamChatClientProvider = inject(STREAM_CHAT_CLIENT_TOKEN);
  private readonly ngZone: NgZone = inject(NgZone);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  private readonly isChatAvailableSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly channelsRefresh$: Subject<void> = new Subject<void>();
  private readonly channelsVersionSignal: WritableSignal<number> = signal<number>(0);

  readonly isChatAvailable$: Observable<boolean> = this.isChatAvailableSubject.asObservable();
  readonly channelsVersion: Signal<number> = this.channelsVersionSignal.asReadonly();

  readonly channels$: Observable<Channel[] | undefined> = merge(
    this.channelProvider.channels$,
    this.channelsRefresh$.pipe(switchMap(() => this.channelProvider.channels$.pipe(take(1))))
  ).pipe(
    map((channels: Channel[] | undefined) => (channels ? this.sortChannels(channels) : channels)),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  readonly activeChannelData$: Observable<ActiveChannelModel> = this.channelProvider.activeChannel$.pipe(
    map(
      (channel: Channel | undefined) =>
        (channel?.data as Record<string, unknown>)?.['orlikfyData'] as ActiveChannelModel
    )
  );

  readonly userData$: Observable<UserModel> = this.userDataProvider.userData$;

  get hasMoreChannels(): boolean {
    let value: boolean = false;
    this.channelProvider.hasMoreChannels$.subscribe((v: boolean) => (value = v)).unsubscribe();
    return value;
  }

  constructor() {
    this.subscribeToChannelRefreshEvents();
  }

  init(): Observable<void> {
    return this.userData$.pipe(
      take(1),
      switchMap((userData: UserModel) => {
        if (!this.hasChatSettings(userData)) {
          this.isChatAvailableSubject.next(false);
          return of(void 0);
        }

        return this.prepareChatSession(userData).pipe(
          switchMap(() => this.loadChannels(userData.chatSettings.chatUserExternalId))
        );
      })
    );
  }

  stopWatching(): void {
    this.channelProvider.deselectActiveChannel();
  }

  openChannel(channelExternalId: string, channelType: ChatChannelType): Observable<void> {
    const navigateToChat = (): Observable<boolean> =>
      from(this.router.navigateByUrl(`${AppRoutes.MESSAGES}/${MessagesRoutes.CHAT}`));

    return this.userData$.pipe(
      take(1),
      switchMap((userData: UserModel) => {
        if (!this.hasChatSettings(userData)) return navigateToChat();

        this.stopWatching();

        return from(this.findChannel(channelExternalId, channelType, userData.chatSettings.chatUserExternalId)).pipe(
          switchMap(async (channel: Channel | undefined) => {
            if (channel) await this.channelProvider.setAsActiveChannel(channel);
          }),
          switchMap(() => navigateToChat())
        );
      }),
      map(() => void 0)
    );
  }

  private prepareChatSession(userData: UserModel): Observable<void> {
    this.stopWatching();
    this.channelProvider.reset();
    this.chatClientProvider.setTranslation('pl', chatTranslation);
    return from(this.ensureClientConnected(userData));
  }

  private async ensureClientConnected(userData: UserModel): Promise<void> {
    const currentUserId: string | undefined = this.chatClientProvider.getCurrentUserId();
    const targetUserId: string = userData.chatSettings.chatUserExternalId;

    if (currentUserId === targetUserId) return;

    if (currentUserId) {
      await this.chatClientProvider.disconnectUser();
    }

    await this.chatClientProvider.init(this.env.chatToken, targetUserId, userData.chatSettings.token);
  }

  private loadChannels(chatUserExternalId: string): Observable<void> {
    return from(
      this.channelProvider.initChannels({
        filters: {
          type: { $in: [ChatChannelType.single, ChatChannelType.multi, ChatChannelType.team] },
          members: { $in: [chatUserExternalId] }
        },
        sort: { last_message_at: -1 },
        options: { watch: true, limit: CHAT_PAGE_SIZE },
        jumpToFirstUnreadChannel: false
      })
    ).pipe(
      tap((channels: Channel[]) => this.isChatAvailableSubject.next(!!channels?.length)),
      map(() => void 0),
      expand(() => (this.hasMoreChannels ? from(this.channelProvider.loadMoreChannels()) : EMPTY)),
      last()
    );
  }

  private async findChannel(
    channelExternalId: string,
    channelType: ChatChannelType,
    userExternalId: string
  ): Promise<Channel | undefined> {
    let offset: number = 0;

    while (true) {
      const channels: Channel[] = await this.chatClientProvider.queryChannels(
        { type: channelType, members: { $in: [userExternalId] } },
        { last_message_at: -1 },
        { watch: true, limit: FIND_CHANNEL_PAGE_SIZE, offset }
      );

      if (channels.length === 0) return undefined;

      const match: Channel | undefined = channels.find((channel: Channel) => channel.id === channelExternalId);
      if (match) return match;

      offset += FIND_CHANNEL_PAGE_SIZE;
    }
  }

  private hasChatSettings(userData: UserModel): boolean {
    return !!(userData?.chatSettings?.chatUserExternalId && userData?.chatSettings?.token);
  }

  private subscribeToChannelRefreshEvents(): void {
    const CHANNEL_REFRESH_EVENTS: ReadonlySet<string> = new Set<string>([
      StreamChatEvent.MessageNew,
      StreamChatEvent.NotificationMessageNew,
      StreamChatEvent.MessageUpdated,
      StreamChatEvent.MessageDeleted,
      StreamChatEvent.NotificationMarkRead,
      StreamChatEvent.NotificationMarkUnread
    ]);

    this.chatClientProvider.events$
      .pipe(
        filter((event: ClientEvent) => CHANNEL_REFRESH_EVENTS.has(event.eventType)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.ngZone.run(() => {
          this.channelsRefresh$.next();
          this.channelsVersionSignal.update((v: number) => v + 1);
        });
      });
  }

  private getLastMessageTime(channel: Channel): number {
    const messages: LocalMessage[] = channel.state?.messages;
    const lastMessageCreatedAt: Date = messages?.[messages.length - 1]?.created_at;
    const raw: unknown = lastMessageCreatedAt ?? channel.state?.last_message_at ?? channel.data?.['last_message_at'];

    if (!raw) return 0;

    const date: Date = raw instanceof Date ? raw : new Date(raw as string);
    return date.getTime();
  }

  private sortChannels(channels: Channel[]): Channel[] {
    return [...channels].sort((a: Channel, b: Channel) => this.getLastMessageTime(b) - this.getLastMessageTime(a));
  }
}
